import { Box, useMediaQuery } from "@mui/material";
import { defaults } from "chart.js";
import {
    Chart as ChartJS,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { useRef, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTheme } from "@emotion/react";
import { formatNumberWithLocale } from "../../utils/charts";

ChartJS.register(ArcElement, Title, Tooltip, Legend, ChartDataLabels);

export default function PieChart({ data, showDataLabels, hideSubregions, title }) {
    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);

    const labels = (data.labels || []).map((l) => (Array.isArray(l) ? l[0] : l));
    const dataset = data.datasets?.[0] || { data: [], backgroundColor: [] };

    const total = dataset.data.reduce((sum, v) => sum + (Number(v) || 0), 0);

    const chartData = {
        labels,
        datasets: [
            {
                ...dataset,
                hoverOffset: 6,
                borderWidth: 0,
            },
        ],
    };

    const options = {
        layout: {
            padding: { top: 35, right: isMobile ? 8 : 12, bottom: 12, left: isMobile ? 5 : 10 },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    padding: isMobile ? 8 : 12,
                    color: theme.palette.primary.fontColor,
                    generateLabels: (chart) => {
                        const original = defaults.plugins.legend.labels.generateLabels;
                        const labelsOriginal = original.call(this, chart);
                        const maxLength = isMobile ? 55 : 95;
                        return labelsOriginal.map((label) => {
                            label.hidden = false;
                            if (typeof label.text === "string" && label.text.length > maxLength) {
                                label.text = label.text.substring(0, maxLength) + "...";
                            }
                            return label;
                        });
                    },
                },
            },
            datalabels: {
                color: theme.palette.primary.fontColor,
                align: "end",
                anchor: "end",
                clamp: true,
                display: showDataLabels ? "auto" : false,
                backgroundColor: "rgba(255, 255, 255, 0)",
                font: { size: 10 },
                padding: 4,
                formatter: (value) => {
                    const v = Number(value) || 0;
                    if (!total || !v) return "";
                    return `${((v / total) * 100).toFixed(1)}%`;
                },
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || "";
                        const value = Number(context.parsed) || 0;
                        const unit = data.info?.unit || "";
                        const pct = total ? ((value / total) * 100).toFixed(1) + "%" : "";
                        return `${label}: ${formatNumberWithLocale(value)} ${unit} (${pct})`;
                    },
                    labelTextColor: () => "#000",
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                footerColor: "#333",
                borderColor: "#ccc",
                borderWidth: "1",
            },
            title: {
                display: false, // will be toggled to true for PNG export
                text: title,
                font: { size: 14, lineHeight: 1.2 },
                padding: { top: 20, left: 0, right: 0, bottom: 10 },
            },
        },
    };

    return (
        <Box className="max-chart-height">
            <Pie ref={ref} options={options} data={chartData} />
        </Box>
    );
}